import { SxProps, Box, Theme, Typography } from "@mui/material";
import { ReactElement, useState } from "react";
import useMeasure from "react-use-measure";

import { palette } from "~frontend/theme";

import ExpandButton from "./ExpandButton";
import { FlexBox } from "./FlexBox";
import ChevronDown from "./icons/ChevronDown";
import ChevronUp from "./icons/ChevronUp";

// NOTE: the current design is for the script preview interface which is a tight
// design that requires negative margins to offset the padding and make the
// border extend to the edges of the container. Whether this should be the default
// may depend on where else this is used. Either way, the sxHeader and sxHeading
// attribute could be use to customize.
const drawerHeaderSx: SxProps<Theme> = {
  marginRight: "-16px",
  marginLeft: "-16px",
  marginBottom: "-10px",
  borderTopColor: palette.midGray,
  borderTopWidth: 1,
  borderTopStyle: "solid",

  paddingX: 2,
  paddingY: "2px",

  "& svg": {
    transform: "scale(0.875)",
  },
};

export default function CollapsibleDrawer({
  title,
  startOpen = true,
  sxHeader = {},
  sxHeading = {},
  children,
  expandText, // if provided, the text to show on the trigger when the drawer is collapsed (and expanded if no collapseText provided)
  collapseText, // if provided, the text to show on the trigger when the drawer is expanded
  variant = "default",
  expandLocation = "top",
  width,
  triggerSx = {},
}: {
  title: React.ReactNode | string;
  startOpen: boolean;
  sxHeader?: SxProps<Theme>;
  sxHeading?: SxProps<Theme>;
  children: React.ReactNode;
  expandText?: string;
  collapseText?: string;
  variant?: "default" | "slim";
  width?: number | string;
  expandLocation?: "top" | "bottom";
  triggerSx?: SxProps<Theme>;
}): ReactElement {
  const [open, setOpenState] = useState(startOpen);

  const isSlim = variant === "slim";

  const sx: SxProps<Theme> = isSlim ? {} : drawerHeaderSx;

  const [childrenRef, { height }] = useMeasure();

  const expandTrigger = (
    <FlexBox key="expand-trigger" alignItems="center" justifyContent="end">
      <Box
        onClick={() => setOpenState(!open)}
        sx={{
          cursor: "pointer",
          "&:hover": {
            color: palette.darkGray,
          },
          ...triggerSx,
        }}
      >
        {(expandText || collapseText) && (
          <Typography variant={isSlim ? "body3" : "body1Bold"}>
            {open ? collapseText || expandText : expandText}
          </Typography>
        )}

        {isSlim ? (
          open ? (
            <ChevronUp sx={{ height: 8 }} />
          ) : (
            <ChevronDown sx={{ height: 8 }} />
          )
        ) : (
          <ExpandButton onClick={() => setOpenState(!open)} open={open} />
        )}
      </Box>
    </FlexBox>
  );

  return (
    <>
      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        onClick={() => !isSlim && setOpenState(!open)}
        sx={
          {
            cursor: !isSlim && "pointer",
            width,
            ...sx,
            ...sxHeader,
          } as SxProps<Theme>
        }
      >
        <Typography
          variant={isSlim ? "body2" : "body2Bold"}
          sx={{ color: palette.darkGray, ...sxHeading }}
        >
          {title}
        </Typography>

        {expandLocation === "top" && expandTrigger}
      </FlexBox>

      <div
        style={{
          overflow: "hidden",
          transition: `height 0.5s`,
          height: open ? height : 0,
        }}
        data-testid="collapsible-drawer"
      >
        <div ref={childrenRef}>{children}</div>
      </div>

      {expandLocation === "bottom" && expandTrigger}
    </>
  );
}
